export const SETTLED_SENT_KEY = "bc_settled_sent";
export const SETTLED_PER_PASS = 8;

type SettlementFlusherOptions = {
  settlements: () => Array<Record<string, unknown>>;
  send: (settlement: Record<string, unknown>) => void;
  local: Storage | null;
  session: Storage | null;
  perPass?: number;
};

const readSent = (storage: Storage | null): string[] => {
  try {
    const raw = storage?.getItem(SETTLED_SENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) && parsed.every((key) => typeof key === "string")
      ? parsed
      : [];
  } catch {
    return [];
  }
};

// ── Settled bets → the backend, ONCE per leg per browser ─────────────────
//
// The history is re-derived on every harvest, so without a sent-set every
// poll would re-report the same settled legs and the won/lost counts on the
// dashboard would climb forever with no new bets behind them.
//
// ⚠️ sessionStorage, not memory: the harvest runs across a session's whole
// life and a page navigation inside the sportsbook rebuilds the widget. Memory
// alone would re-send everything on every route change. localStorage also carries
// that sent-set into a new tab session in the same browser.
//
// A generous ceiling per pass, not per session: a first harvest can hold
// a year of history, and firing all of it at once is a burst against our
// own API for data that is not going anywhere.
//
// ⛔ MEASURED 2026-10-02 17:00–18:00 UTC on production: 56,308 of 60,237 events
// (93%) were `bet_settled`; new tab sessions re-sent the browser's whole history.
// nginx allows 10 r/s with burst 30, shared with the poll and every other event,
// so each pass is capped at 8 rather than the former 25-post burst.
export function makeSettlementFlusher(opts: SettlementFlusherOptions): () => void {
  const sent = new Set([...readSent(opts.local), ...readSent(opts.session)]);
  const perPass = opts.perPass ?? SETTLED_PER_PASS;

  return (): void => {
    try {
      // Re-read on every pass: another tab of this browser may have sent since,
      // and writing our set back without its keys would make both resend.
      for (const key of [...readSent(opts.local), ...readSent(opts.session)]) sent.add(key);
      let added = 0;
      for (const settlement of opts.settlements()) {
        const key = String(settlement.order) + ":" + String(settlement.event_id);
        if (sent.has(key)) continue;
        sent.add(key);
        opts.send(settlement);
        added++;
        if (added >= perPass) break;
      }
      if (!added) return;

      const remembered = JSON.stringify([...sent].slice(-2000));
      try {
        if (!opts.local) throw new Error("localStorage unavailable");
        opts.local.setItem(SETTLED_SENT_KEY, remembered);
      } catch {
        try {
          opts.session?.setItem(SETTLED_SENT_KEY, remembered);
        } catch {
          /* private mode / quota */
        }
      }
    } catch {
      /* settlements and event delivery must never break the widget */
    }
  };
}
